import type { CameraPath, PathPoint, PathVec3 } from "@/lib/location-scene-3d/camera-path"
import { yawPitchFromDirection } from "@/lib/location-scene-3d/camera-path-spline"
import {
  sectorSizeMeters,
  sectorWorldCenter,
  worldToSectorIndex,
  type SceneCell,
  type ScenePlatform,
} from "@/lib/location-scene-3d/platform"
import type { SceneCameraState } from "@/lib/location-scene-3d/scene-state"

/** Just above the floor so the dot sits on the tile, not inside it. */
export const CAMERA_PLACE_DOT_LIFT = 0.04
/** Sphere radius at chrome 1. Same order as a trajectory knot, one and a half times smaller. */
export const CAMERA_PLACE_DOT_RADIUS = 0.16 / 1.5
export const CAMERA_PLACE_HOVER_SCALE = 1.65
/** Idle place dots. Hover and the chosen sector stay opaque. */
export const CAMERA_PLACE_DOT_OPACITY = 0.55
/** Eight times the fly-look step: yaw and pitch, radians per pixel. */
export const CAMERA_PLACE_LOOK_RAD_PER_PX = 0.0272
/** Camera gizmo height 0.9 scaled by the overlay icon scale 2, before chrome. */
export const CAMERA_PLACE_SUBJECT_HEIGHT = 1.8
/** Placed camera fills this share of the vertical frame. */
export const CAMERA_PLACE_FRAME_FRACTION = 0.25
/**
 * Trajectory icon hides within 3 m. Framing stays outside that, otherwise the
 * camera disappears the moment the view arrives.
 */
export const CAMERA_PLACE_MIN_FRAME_DISTANCE = 3.2

export type CameraPlaceMarker = {
  sectorX: number
  sectorZ: number
  /** Top of the column. Floor is 0. */
  surfaceY: number
  pos: PathVec3
}

type PlatformSize = Pick<ScenePlatform, "sectors_x" | "sectors_z" | "sector_size" | "unit">

export function cameraPlaceMarkers(
  platform: PlatformSize | null | undefined,
  cells: ReadonlyArray<SceneCell>,
  lift = CAMERA_PLACE_DOT_LIFT,
): CameraPlaceMarker[] {
  if (!platform || !(platform.sectors_x > 0) || !(platform.sectors_z > 0)) return []
  const heights = new Map(cells.map((cell) => [`${cell.x}:${cell.z}`, cell.height]))
  const sectorM = sectorSizeMeters(platform)
  const markers: CameraPlaceMarker[] = []
  for (let z = 0; z < platform.sectors_z; z += 1) {
    for (let x = 0; x < platform.sectors_x; x += 1) {
      const height = heights.get(`${x}:${z}`) ?? 0
      const surfaceY = height > 0 ? height * sectorM : 0
      const [wx, wz] = sectorWorldCenter(platform, x, z)
      markers.push({ sectorX: x, sectorZ: z, surfaceY, pos: [wx, surfaceY + lift, wz] })
    }
  }
  return markers
}

/** Marker on a sector, including a raised column. Null when that sector has no marker. */
export function cameraPlaceMarkerIndex(
  markers: readonly CameraPlaceMarker[],
  sectorX: number,
  sectorZ: number,
): number | null {
  const index = markers.findIndex((marker) => marker.sectorX === sectorX && marker.sectorZ === sectorZ)
  return index >= 0 ? index : null
}

/**
 * Ground-plane XZ. A raised column is not a floor click: the plane passes through the block,
 * so a ray through the volume must not select the roof.
 */
export function cameraPlaceHitIndex(
  platform: PlatformSize | null | undefined,
  cells: ReadonlyArray<SceneCell>,
  markers: readonly CameraPlaceMarker[],
  worldX: number,
  worldZ: number,
): number | null {
  if (!platform || markers.length === 0) return null
  const sector = worldToSectorIndex(platform, worldX, worldZ)
  if (!sector) return null
  const height = cells.find((cell) => cell.x === sector.x && cell.z === sector.z)?.height ?? 0
  if (height > 0) return null
  return cameraPlaceMarkerIndex(markers, sector.x, sector.z)
}

/**
 * Keep world Y when the camera already clears the next surface.
 * A taller column lifts by the clearance above the previous surface, so the eye does not sit in the roof.
 */
export function cameraPlaceEyeY(previousY: number, previousSurfaceY: number, nextSurfaceY: number): number {
  if (previousY >= nextSurfaceY) return previousY
  const clearance = Math.max(0, previousY - previousSurfaceY)
  return nextSurfaceY + clearance
}

/** Drag on a sector turns the camera being placed. Positive x is yaw to the right, same as fly look. */
export function cameraPlaceAimFromDrag(
  start: { yaw: number; pitch: number },
  dx: number,
  dy: number,
  pitchMax: number,
): { yaw: number; pitch: number } {
  const pitch = start.pitch - dy * CAMERA_PLACE_LOOK_RAD_PER_PX
  return {
    yaw: start.yaw - dx * CAMERA_PLACE_LOOK_RAD_PER_PX,
    pitch: Math.min(pitchMax, Math.max(-pitchMax, pitch)),
  }
}

/**
 * View pose that looks at `target` from the current approach.
 * Stops where the subject is about a quarter of the frame height.
 * Already closer than that pulls back to the same distance.
 */
export function framePoseAroundTarget(
  viewPos: PathVec3,
  target: PathVec3,
  fovDeg: number,
  subjectHeight: number,
  minDistance = 0,
): { pos: PathVec3; yaw: number; pitch: number } {
  const fov = (Math.max(fovDeg, 1) * Math.PI) / 180
  const alpha = fov * CAMERA_PLACE_FRAME_FRACTION
  const height = Math.max(subjectHeight, 1e-3)
  const framed = height / 2 / Math.tan(alpha / 2)
  const dist = Math.max(framed, minDistance, 1e-3)
  let dx = viewPos[0] - target[0]
  let dy = viewPos[1] - target[1]
  let dz = viewPos[2] - target[2]
  let len = Math.hypot(dx, dy, dz)
  if (len < 1e-4) {
    dx = 0
    dy = 0.35
    dz = 1
    len = Math.hypot(dx, dy, dz)
  }
  const scale = dist / len
  const pos: PathVec3 = [target[0] + dx * scale, target[1] + dy * scale, target[2] + dz * scale]
  const look = yawPitchFromDirection([target[0] - pos[0], target[1] - pos[1], target[2] - pos[2]])
  return { pos, yaw: look.yaw, pitch: look.pitch }
}

/** XZ from the sector center. Y is eye height, so the next trajectory point keeps this height. */
export function cameraPoseAtPlace(
  marker: CameraPlaceMarker,
  eyeY: number,
  look: { yaw: number; pitch: number; fov: number },
): SceneCameraState {
  return {
    pos: [marker.pos[0], eyeY, marker.pos[2]],
    yaw: look.yaw,
    pitch: look.pitch,
    fov: look.fov,
    mode: "fly",
  }
}

/** First click seeds point 0. A later click in the same mode replaces it. */
export function replaceFirstPathPoint(points: readonly PathPoint[], pos: PathVec3): PathPoint[] {
  const placed: PathPoint = { pos: [pos[0], pos[1], pos[2]], tangent: null }
  if (points.length === 0) return [placed]
  return points.map((point, index) => {
    if (index === 0) return placed
    return {
      pos: [...point.pos] as PathVec3,
      tangent: point.tangent ? ([...point.tangent] as PathVec3) : null,
    }
  })
}

/** One authored point uses the saved start aim. Without a point the icon is not shown. */
export function cameraPlaceAimFromStart(
  start: { yaw: number; pitch: number } | null | undefined,
  pointCount: number,
): { yaw: number; pitch: number } | null {
  if (!start || pointCount < 1) return null
  return { yaw: start.yaw, pitch: start.pitch }
}

/** No point, sample, or look key: the take was opened and the camera was never placed. */
export function cameraPlaceTakeIsEmpty(path: CameraPath | null | undefined): boolean {
  if (!path) return true
  if ((path.authoring?.points.length ?? 0) > 0) return false
  if ((path.samples?.length ?? 0) > 0) return false
  if ((path.look_keys?.length ?? 0) > 0) return false
  return true
}

/** Corner preview while placing: live drag, or a take that already has a point. A saved start alone does not. */
export function cameraPlaceMonitorVisible(preview: boolean, pointCount: number): boolean {
  return preview || pointCount > 0
}

const CAMERA_PLACE_AIM_CHANGE_RAD = 0.001

/** True when the release heading is not the heading at pointer down. A full yaw turn is the same heading. */
export function cameraPlaceAimChanged(
  start: { yaw: number; pitch: number },
  next: { yaw: number; pitch: number },
): boolean {
  const yawDelta = Math.atan2(Math.sin(next.yaw - start.yaw), Math.cos(next.yaw - start.yaw))
  const yawChanged = Math.abs(yawDelta) > CAMERA_PLACE_AIM_CHANGE_RAD
  const pitchChanged = Math.abs(next.pitch - start.pitch) > CAMERA_PLACE_AIM_CHANGE_RAD
  return yawChanged || pitchChanged
}

/**
 * Identity of one corner-preview shot. Thousandths so float noise does not
 * force another scene render while the placed camera is still.
 */
export function cameraPlaceMonitorFrameKey(
  pose: { pos: PathVec3; yaw: number; pitch: number },
  fov: number,
  width: number,
  height: number,
): string {
  const milli = (value: number) => Math.round(value * 1000)
  return [
    milli(pose.pos[0]),
    milli(pose.pos[1]),
    milli(pose.pos[2]),
    milli(pose.yaw),
    milli(pose.pitch),
    milli(fov),
    width,
    height,
  ].join(":")
}

/**
 * One seeded point must keep draw on, so the next trajectory click is point 2
 * and the spline joins it to the placed camera. Two points edit knots instead.
 */
export function trajectoryDrawWithSeed(pointCount: number, sampleCount: number): boolean {
  if (pointCount >= 2) return false
  if (pointCount === 1) return true
  return sampleCount < 2
}
